import type { AppRouter } from '@owlbert-backend/trpc-client'
import {
  DEFAULT_USER_DOCUMENT_KB_CHUNK_SIZE,
  Knowledgebase,
  TrpcAutoEmbeddingEmbedder,
  TrpcCloudVectorDb,
  buildUserDocumentKbParentDocument,
  indexUserDocumentParentIntoKnowledgebase,
  isNonIndexableUserDocumentContent,
  resolveUserDocumentKbSourceId,
} from '@owlbert/core'
import { webLog } from '../logging/webLog'

export type BrowserHostedUserDocForKb = {
  id: string
  name: string
  content: string
  metadata?: Record<string, unknown> | null
  mimeType?: string | null
}

/** tRPC slices needed to chunk, embed, and insert hosted library docs into the cloud KB. */
export type BrowserKbIndexClients = {
  embeddings: AppRouter['embeddings']
  knowledgebase: AppRouter['knowledgebase']
}

/**
 * Re-index one hosted library document into the cloud knowledgebase (same `source_id` scheme as desktop).
 * Uses `@owlbert/core` chunking + `TrpcCloudVectorDb` + backend embeddings.
 */
export async function indexBrowserHostedUserDocumentToKb(opts: {
  clients: BrowserKbIndexClients
  workspaceId: string
  doc: BrowserHostedUserDocForKb
}): Promise<void> {
  const { id, name, content } = opts.doc
  const contentStr = typeof content === 'string' ? content : ''

  if (isNonIndexableUserDocumentContent(contentStr)) {
    webLog('info', 'kb-index', 'skip index (no extractable text)', { id, name })
    return
  }

  const embedder = new TrpcAutoEmbeddingEmbedder(opts.clients.embeddings)
  const vectorDb = new TrpcCloudVectorDb({
    client: { knowledgebase: opts.clients.knowledgebase },
    workspaceId: opts.workspaceId,
    embedder,
  })
  const chunkSize = DEFAULT_USER_DOCUMENT_KB_CHUNK_SIZE
  const kb = new Knowledgebase({
    vectorDb: () => vectorDb,
    sources: [],
    chunkSize,
    chunkOverlap: Math.floor(chunkSize * 0.1),
    workspaceId: opts.workspaceId,
  })

  const parent = buildUserDocumentKbParentDocument({
    kbDocumentId: id,
    name,
    content: contentStr,
    metadata: opts.doc.metadata,
    workspaceId: opts.workspaceId,
    mimeType: opts.doc.mimeType ?? null,
  })
  const sourceId = String(parent.metadata?.source_id ?? '')

  webLog('info', 'kb-index', 'start', { id, name, contentChars: contentStr.length, sourceId })

  await indexUserDocumentParentIntoKnowledgebase({
    knowledgebase: kb,
    vectorDb,
    workspaceId: opts.workspaceId,
    parent,
  })

  webLog('info', 'kb-index', 'done', { id, name, sourceId })
}

/** Remove all KB rows for this library document (matches desktop `user_document:<id>` / custom `source_id`). */
export async function deleteBrowserHostedUserDocumentFromKb(opts: {
  clients: Pick<BrowserKbIndexClients, 'knowledgebase'>
  workspaceId: string
  documentId: string
  metadata?: Record<string, unknown> | null
}): Promise<void> {
  const sourceId = resolveUserDocumentKbSourceId(opts.documentId, opts.metadata?.source_id as string | undefined)
  await opts.clients.knowledgebase.deleteBySourceId.mutate({
    workspaceId: opts.workspaceId,
    sourceId,
  })
  webLog('info', 'kb-index', 'deleted kb rows for source', { documentId: opts.documentId, sourceId })
}
